import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import NoiseAwareRoundedIcon from "@mui/icons-material/NoiseAwareRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function NoiseGateLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Noise Gate in Music Production: What It Is and How It Works",
      description:
        "Learn what a noise gate does, how it reduces unwanted noise, the difference between a gate and expander, and how gates are used on guitars, drums, vocals, and live sound.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/noise-gate",
      articleSection: "Audio Education",
      keywords: [
        "noise gate",
        "what does a noise gate do",
        "audio gate",
        "gate vs expander",
        "guitar noise gate",
        "drum gate",
        "vocal gate",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A noise gate reduces or mutes audio when the signal falls below a set threshold, helping remove hiss, hum, bleed, and unwanted background noise.",
      icon: <VolumeOffRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "A gate can clean up noisy recordings and tighten performances by letting wanted sound through while reducing unwanted low-level noise.",
      icon: <NoiseAwareRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Guitars, drums, vocals, podcasts, live sound, broadcast chains, and any source where background noise or bleed becomes distracting.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const gateTypes = [
    {
      name: "Standard Noise Gate",
      description:
        "The classic gate closes when the signal drops below threshold and opens again when the signal rises above it.",
    },
    {
      name: "Downward Expander",
      description:
        "A gentler alternative that reduces low-level audio instead of shutting it off completely, often sounding more natural.",
    },
    {
      name: "Sidechain Gate",
      description:
        "Uses a separate trigger source to control when the gate opens or closes, which is useful for creative rhythmic effects and tighter control.",
    },
    {
      name: "Multiband Gate",
      description:
        "Applies gating only to selected frequency ranges, which can be helpful when noise lives in one part of the spectrum.",
    },
    {
      name: "Lookahead Gate",
      description:
        "Anticipates transients slightly before they happen, helping preserve attacks that might otherwise get clipped.",
    },
    {
      name: "Rhythmic / Creative Gate",
      description:
        "Used as an effect rather than just cleanup, creating pulses, chops, and timed patterns in synths, pads, and other sources.",
    },
  ];

  const gateConcepts = [
    {
      name: "Threshold",
      description:
        "The level that determines when the gate opens or closes. If the signal falls below this point, the gate starts reducing it.",
    },
    {
      name: "Open and Closed States",
      description:
        "When the gate is open, the signal passes through. When it is closed, the signal is reduced or muted depending on the settings.",
    },
    {
      name: "Attack and Release",
      description:
        "Attack controls how quickly the gate opens. Release controls how quickly it closes after the signal falls below threshold.",
    },
    {
      name: "Naturalness vs Tightness",
      description:
        "A more aggressive gate can sound very tight and controlled, but too much can chop off transients or make the source sound unnatural.",
    },
  ];

  const controls = [
    {
      name: "Threshold",
      description:
        "Sets the point where the gate starts working. Higher threshold means more of the signal gets reduced.",
    },
    {
      name: "Attack",
      description:
        "Controls how quickly the gate opens. Too slow can dull the front of a note or hit, while fast settings preserve more attack.",
    },
    {
      name: "Hold",
      description:
        "Keeps the gate open briefly after the signal drops below threshold, which can help avoid chattering or rapid opening and closing.",
    },
    {
      name: "Release",
      description:
        "Controls how quickly the gate closes. Very fast release can sound abrupt, while slower release can sound smoother.",
    },
    {
      name: "Range",
      description:
        "Sets how much reduction happens when the gate closes. Some gates mute completely, while others only reduce the level by a chosen amount.",
    },
    {
      name: "Sidechain Filter",
      description:
        "Lets you control what frequencies trigger the gate, which is useful for preventing low rumble or cymbal bleed from opening it unexpectedly.",
    },
  ];

  const commonUses = [
    "Reducing amp hiss and pickup noise on distorted guitar",
    "Tightening toms and snare by reducing bleed between hits",
    "Cleaning up vocal pauses in noisy recordings",
    "Controlling room noise in podcast or spoken-word sessions",
    "Making live sound rigs quieter between phrases or songs",
    "Creating rhythmic chopping effects with sidechain or tempo-synced gating",
  ];

  const bestPractices = [
    "Set the threshold only as high as needed. Too much gating can make a track sound unnatural or lifeless.",
    "Use hold and release to make the gate behave smoothly rather than chattering open and closed.",
    "Listen for clipped transients at the beginning of notes or drum hits. That often means the attack is too slow or the threshold is too high.",
    "Try a downward expander if full gating sounds too obvious.",
    "Adjust the gate while listening in the full mix, not only in solo, because a perfectly clean isolated track is not always the best musical result.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compressors reduce dynamic range, while gates work in the opposite direction by reducing low-level material.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "Explore how limiting controls peaks, while gating controls low-level noise and bleed.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "See how EQ and filtering can help a gate behave better by removing frequencies that should not trigger it.",
    },
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "Understand why high-gain guitar sounds often need gating because distortion can greatly increase hiss and noise.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Noise Gate | Parish Audio Learning"
        description="Learn what a noise gate does, how it reduces unwanted noise, the difference between a gate and expander, and how gates are used in music production and live sound."
        canonical="https://parishaudio.com/learning/noise-gate"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Noise Gate in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                A noise gate is a dynamics processor used to reduce unwanted low-level
                sound. When the signal drops below a chosen threshold, the gate closes
                and turns the signal down, helping remove hiss, hum, bleed, room noise,
                or amp noise that would otherwise remain audible between notes or phrases.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                Noise gates are especially useful on distorted guitars, drum close mics,
                vocals recorded in imperfect rooms, and live sound systems. Used well,
                they can make audio sound cleaner and tighter. Used too aggressively,
                they can sound choppy, artificial, or like the beginning and end of notes
                are being cut off.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                What is a noise gate?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A noise gate is a processor that reduces audio when the input signal falls
                below a threshold. In its simplest form, it acts like an automatic mute
                that opens when the wanted sound is present and closes when only unwanted
                low-level noise remains.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                This makes gates useful for sources that alternate between wanted sound and
                unwanted noise. A high-gain guitar tone may hiss loudly when no notes are
                being played. A snare mic may contain hi-hat bleed between hits. A gate can
                reduce those unwanted moments without constantly affecting the main sound.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Gates are part of the broader family of dynamics processors, but they work
                differently from compressors and limiters. Instead of reducing the loudest
                peaks, a gate mainly targets the quietest parts of a signal.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How a noise gate works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {gateConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                Imagine a guitar track with a lot of hiss. When a note is played, the guitar
                is loud enough to rise above the threshold, so the gate opens and lets the
                sound pass. When the note dies away and only hiss remains, the gate closes
                and reduces that unwanted noise.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common types of gates
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {gateTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Noise gate vs expander
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Similar idea, different intensity
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A noise gate usually applies stronger reduction once the signal falls
                    below threshold. A downward expander is gentler. Instead of sharply
                    muting the signal, it gradually turns low-level sound down.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Because of that, expanders often sound more natural on vocals, acoustic
                    instruments, and subtle cleanup jobs. Gates are often preferred when
                    stronger separation or tighter silence is needed.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common noise gate controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How noise gates are used in mixing and live sound
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                One of the biggest benefits of gating is separation. In a dense mix, cleaner
                silences can make instruments feel more focused and controlled. In live sound,
                gates can help reduce stage wash and keep the system quieter between phrases.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                When a gate is set too aggressively
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "The start of notes or drum hits may get clipped, making the sound feel weak or unnatural.",
                    "Sustains and tails may disappear too quickly, which can make the source feel smaller and less musical.",
                    "The gate may chatter or flutter open and closed in a distracting way.",
                    "A vocal or instrument may sound too sterile if natural room sound is removed completely.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Best practices for using a gate
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                A noise gate can be one of the most practical cleanup tools in audio. It
                helps reduce unwanted noise, tighten performances, and make recordings feel
                cleaner and more controlled.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                The trick is to use enough gating to solve the problem without stripping away
                natural tone, sustain, and feel. When dialed in carefully, a gate can be
                almost invisible. When pushed too far, it quickly becomes obvious.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}